import { createContext, useCallback, useEffect, useMemo, useReducer } from "react";
import { UmbracoNode } from "../../models/UmbracoNode"
import { fetchAPI, paginateItems } from "../../Helpers/utils";
import { FeedModel } from "../../models/FeedModel";

//----------------------------------------
// REDUCER
export enum FeedActionEnum {
  SET_PAGESIZE,
  SET_FEED_ITEMS,
  GET_PAGED_ITEMS
}

export type FeedState = {
  items: UmbracoNode[],
  pagedItems: UmbracoNode[],
  pageSize: number,
  currentPage: number,
  totalRecords: number
}

const initialFeedState = {
  items: [],
  pagedItems: [],
  pageSize: 6,
  currentPage: 1,
  totalRecords: 0
}

export type FeedAction = |
{ action: FeedActionEnum.SET_PAGESIZE | FeedActionEnum.GET_PAGED_ITEMS, payload: number }
| { action: FeedActionEnum.SET_FEED_ITEMS, payload: UmbracoNode[] }


export const FeedStateReducer = (state: FeedState, event: FeedAction) => {
  switch (event.action) {
    case FeedActionEnum.SET_PAGESIZE:
      return {
        ...state,
        pageSize: event.payload
      }
    
    case FeedActionEnum.SET_FEED_ITEMS:
      return {
        ...state,
        currentPage: 1,
        items: [...event.payload],
        pagedItems: [...paginateItems([...event.payload], 1, state.pageSize)],
        totalRecords: event.payload.length
      }

    case FeedActionEnum.GET_PAGED_ITEMS:
      return {
        ...state,
        currentPage: event.payload,
        pagedItems: [...paginateItems([...state.items], event.payload, state.pageSize)]
      }
    
    default:
      return initialFeedState;
  }
}

//----------------------------------------
// CONTEXT
export type FeedContextType = {
  nodes: UmbracoNode[],
  totalRecords: number,
  currentPage: number,
  pageSize: number,

  loadItems: (page: number) => void
}

export const FeedContext = createContext<FeedContextType | null>(null);

//----------------------------------------
// PROVIDER
export type FeedProviderModel = {
  children: React.ReactNode,
  feed: FeedModel
}

export default function FeedProvider({ children, feed }: FeedProviderModel) {
  const [state, dispatch] = useReducer(FeedStateReducer, initialFeedState);

  useEffect(() => {
    async function getFeed() {
      const data = await fetchAPI<UmbracoNode[]>(`/api/feed`, {
        sources: feed.sources ? feed.sources.join(',') : '',
        contentTypes: feed.allowedContentTypes ? feed.allowedContentTypes.join(',') : '',
        pageSize: feed.itemCount,
        pageId: feed.pageId,
        query: feed.query,
        sortBy: feed.sortBy,
        categories: feed.categories ? feed.categories.join(',') : '',
      });

      dispatch({ action: FeedActionEnum.SET_FEED_ITEMS, payload: data });
    }
    
    dispatch({ action: FeedActionEnum.SET_PAGESIZE, payload: feed.pageSize });
    getFeed();
  }, []);

  const loadItems = useCallback((page: number) => {
    dispatch({ action: FeedActionEnum.GET_PAGED_ITEMS, payload: page });
  }, []);

  const values = useMemo(() => {
    return {
      nodes: state.pagedItems,
      totalRecords: state.totalRecords,
      currentPage: state.currentPage,
      pageSize: state.pageSize,

      loadItems
    }
  }, [
    state,

    loadItems
  ]);

  return (
    <FeedContext.Provider value={values}>
      {children}
    </FeedContext.Provider>
  )
}